// Validation utilities for questionnaire forms

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

// Validate if a question is answered (mandatory check)
export const validateMandatoryQuestion = (question: any): ValidationResult => {
  if (!question.is_mandatory) {
    return { isValid: true };
  }

  const answers = question.customer_answer || [];
  const activeOptions = question.available_options?.filter(
    option => option.is_active && !option.is_deleted
  ) || [];
  
  // For layouts with multiple inputs, check if ALL inputs are filled
  if (['WEIGHT_LAYOUT', 'HEIGHT_LAYOUT', 'BODY_LAYOUT', 'MEDICAL_LAYOUT'].includes(question.action_type)) {
    // All active options must have answers
    const answeredOptionIds = answers.map(ans => ans.selected_option);
    const missingOptions = activeOptions.filter(opt => !answeredOptionIds.includes(opt.id));
    
    if (missingOptions.length > 0) {
      return { isValid: false, error: 'Please fill all required fields' };
    }
    
    // Check if all answers have valid content
    const hasValidAnswers = answers.every(answer => {
      if (answer.answer && typeof answer.answer === 'string') {
        return answer.answer.trim().length > 0;
      }
      return false;
    });
    
    if (!hasValidAnswers) {
      return { isValid: false, error: 'Please fill all required fields' };
    }
    
    return { isValid: true };
  }
  
  // For BLOOD_PRESSURE_LAYOUT, check first two inputs OR third checkbox
  if (question.action_type === 'BLOOD_PRESSURE_LAYOUT') {
    const inputOptions = activeOptions.slice(0, 2);
    const checkboxOption = activeOptions[2];
    
    // Check if checkbox is selected
    const checkboxSelected = answers.some(ans => ans.selected_option === checkboxOption?.id);
    
    if (checkboxSelected) {
      return { isValid: true };
    }
    
    // Otherwise, both input fields must be filled
    const inputAnswers = answers.filter(ans => 
      inputOptions.some(opt => opt.id === ans.selected_option)
    );
    
    if (inputAnswers.length !== inputOptions.length) {
      return { isValid: false, error: 'Please fill both fields or select the checkbox option' };
    }
    
    const hasValidInputs = inputAnswers.every(answer => 
      answer.answer && answer.answer.trim().length > 0
    );
    
    if (!hasValidInputs) {
      return { isValid: false, error: 'Please fill both fields or select the checkbox option' };
    }
    
    return { isValid: true };
  }
  
  // For single answer layouts (including DATEPICKER_LAYOUT)
  if (answers.length === 0) {
    return { isValid: false, error: 'This field is required' };
  }

  // Check if any answer has actual content
  const hasValidAnswer = answers.some(answer => {
    if (typeof answer === 'string') {
      return answer.trim().length > 0;
    }
    if (typeof answer === 'object' && answer !== null) {
      // Check if answer object has meaningful content
      if (answer.answer && typeof answer.answer === 'string') {
        return answer.answer.trim().length > 0;
      }
      // For file uploads, check if file exists
      if (answer.file) {
        return true;
      }
      return Object.values(answer).some(value => 
        value !== null && value !== undefined && String(value).trim().length > 0
      );
    }
    return answer !== null && answer !== undefined;
  });

  if (!hasValidAnswer) {
    return { isValid: false, error: 'This field is required' };
  }

  return { isValid: true };
};

// Validate numeric input
export const validateNumericInput = (value: string): ValidationResult => {
  if (!value || value.trim() === '') {
    return { isValid: true }; // Empty is valid, mandatory check handles required
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return { isValid: false, error: 'Please enter a valid number' };
  }

  if (numValue < 0) {
    return { isValid: false, error: 'Please enter a positive number' };
  }

  return { isValid: true };
};

// Validate date format DD/MM/YYYY
export const validateDateFormat = (value: string): ValidationResult => {
  if (!value || value.trim() === '') {
    return { isValid: true }; // Empty is valid, mandatory check handles required
  }

  const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = value.match(dateRegex);
  
  if (!match) {
    return { isValid: false, error: 'Please enter date in DD/MM/YYYY format' };
  }

  const [, day, month, year] = match;
  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);

  // Basic date validation
  if (monthNum < 1 || monthNum > 12) {
    return { isValid: false, error: 'Please enter a valid month (01-12)' };
  }

  if (dayNum < 1 || dayNum > 31) {
    return { isValid: false, error: 'Please enter a valid day (01-31)' };
  }

  if (yearNum < 1900 || yearNum > new Date().getFullYear()) {
    return { isValid: false, error: 'Please enter a valid year' };
  }

  // Check if date is valid
  const date = new Date(yearNum, monthNum - 1, dayNum);
  if (date.getDate() !== dayNum || date.getMonth() !== monthNum - 1 || date.getFullYear() !== yearNum) {
    return { isValid: false, error: 'Please enter a valid date' };
  }

  return { isValid: true };
};

// Validate file upload
export const validateFileUpload = (question: any): ValidationResult => {
  if (!question.is_mandatory) {
    return { isValid: true };
  }

  const answers = question.customer_answer || [];
  const hasFile = answers.some(answer => answer.file || answer.answer === 'Uploaded');

  if (!hasFile) {
    return { isValid: false, error: 'Please upload a file' };
  }

  return { isValid: true };
};

// Validate multi-select mandatory
export const validateMultiSelectMandatory = (question: any): ValidationResult => {
  if (!question.is_mandatory || !question.is_multi_select) {
    return { isValid: true };
  }

  const answers = question.customer_answer || [];
  
  if (answers.length === 0) {
    return { isValid: false, error: 'Please select at least one option' };
  }

  return { isValid: true };
};

// Main validation function for a question
export const validateQuestion = (question: any): ValidationResult => {
  // First check if mandatory question is answered
  const mandatoryResult = validateMandatoryQuestion(question);
  if (!mandatoryResult.isValid) {
    return mandatoryResult;
  }

  // Skip further validation if not answered (and not mandatory)
  const answers = question.customer_answer || [];
  if (answers.length === 0) {
    return { isValid: true };
  }

  // Validate based on action type
  switch (question.action_type) {
    case 'WEIGHT_LAYOUT':
    case 'HEIGHT_LAYOUT':
    case 'BODY_LAYOUT':
      // Validate numeric inputs
      for (const answer of answers) {
        if (answer.answer && typeof answer.answer === 'string') {
          const numResult = validateNumericInput(answer.answer);
          if (!numResult.isValid) {
            return numResult;
          }
        }
      }
      break;
    case 'BLOOD_PRESSURE_LAYOUT':
      // Validate numeric inputs
      for (const answer of answers) {
        if (answer.answer && typeof answer.answer === 'string' && !isNaN(answer.answer)) {
          const numResult = validateNumericInput(answer.answer);
          if (!numResult.isValid) {
            return numResult;
          }
        }
      }
      break;

    case 'DATEPICKER_LAYOUT':
      // Validate date format
      for (const answer of answers) {
        if (answer.answer && typeof answer.answer === 'string') {
          const dateResult = validateDateFormat(answer.answer);
          if (!dateResult.isValid) {
            return dateResult;
          }
        }
      }
      break;

    case 'CAMERA_LAYOUT':
      // Validate file upload
      return validateFileUpload(question);

    case 'LIST_LAYOUT':
      // Validate multi-select mandatory
      if (question.is_multi_select) {
        return validateMultiSelectMandatory(question);
      }
      break;

    default:
      // For other layouts, just check mandatory
      break;
  }

  return { isValid: true };
};

// Validate all questions in current category
export const validateCurrentCategory = (questions: any[]): { isValid: boolean; errors: Record<number, string> } => {
  const errors: Record<number, string> = {};
  let isValid = true;

  questions.forEach(question => {
    const result = validateQuestion(question);
    if (!result.isValid) {
      errors[question.id] = result.error || 'Invalid input';
      isValid = false;
    }
  });

  return { isValid, errors };
};